import jsPDF from 'jspdf';
import { fetchDistrictMillDistances } from '../../market-intelligence/utils';
import {
  LOGO_URL, loadImageAsDataURL, drawWatermark, drawHeader, drawFooter,
  drawCTAButtons, drawNearbyMillsDistance, CTA_BLOCK_HEIGHT, FOOTER_RESERVED_HEIGHT, shortName,
} from '../../market-intelligence/shareExport';

// jsPDF's default font (Helvetica, one of the 14 standard PDF base fonts) has no glyph for
// ₹ (₹) — WinAnsiEncoding predates that codepoint — so it renders as a broken/fallback
// character rather than the rupee sign. shareExport.js's other PDF generators already work
// around this by falling back to "Rs. " whenever no custom Unicode font is loaded (see
// pdfCurrencySymbol there); this file never loaded a custom font at all, so every raw ₹ here
// was silently corrupted. Matching that same fallback rather than paying for font loading this
// generator doesn't otherwise need.
const RS = 'Rs. ';

async function shareOrDownloadBlob(blob, fileName, mimeType, title, text) {
  const file = new File([blob], fileName, { type: mimeType });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return;
    } catch (e) {
      // user cancelled - fall through to download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export async function generateMandiFactoryPDF({ factory, action }) {
  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842; // A4 in points, matches every other SugarHathi export
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  const marginX = 36;
  const contentW = w - marginX * 2;

  drawWatermark(doc, w, h);
  drawHeader(doc, w, 'SugarHathi Mandi \u00b7 Factory Report', logoDataUrl, 'Mandi Factory Report');

  // 1. Name + Address
  doc.setFontSize(15);
  doc.setTextColor(17, 17, 17);
  doc.setFont(undefined, 'bold');
  const titleLines = doc.splitTextToSize(factory.name, contentW);
  let cursorY = 82;
  titleLines.forEach((line) => { doc.text(line, marginX, cursorY); cursorY += 17; });

  doc.setFontSize(8.5);
  doc.setTextColor(120, 113, 108);
  doc.setFont(undefined, 'normal');
  doc.text(`${factory.district}, ${factory.state} \u00b7 Code ${factory.millCode}`, marginX, cursorY - 4);
  cursorY += 8;

  if (factory.address) {
    doc.setFontSize(9);
    doc.setTextColor(68, 64, 60);
    doc.setFont(undefined, 'normal');
    const addrLines = doc.splitTextToSize(factory.address, contentW);
    addrLines.forEach((line) => { doc.text(line, marginX, cursorY); cursorY += 12; });
  }
  cursorY += 10;

  // 2. Current Quota — the single most recent figure (a newer fortnight replaces the last
  // complete month here rather than sitting alongside it — see mandi/data.js)
  if (factory.quotaQty != null) {
    doc.setFillColor(253, 246, 233);
    doc.roundedRect(marginX, cursorY, contentW, 44, 6, 6, 'F');
    doc.setFontSize(7.5);
    doc.setTextColor(146, 64, 14);
    doc.setFont(undefined, 'normal');
    doc.text(`${factory.quotaMonthLabel || 'Current'} ${factory.quotaIsFortnight ? 'Fortnight Quota' : 'Quota'}`, marginX + 10, cursorY + 17);
    doc.setFontSize(16);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text(`${factory.quotaQty.toLocaleString('en-IN')} MT`, marginX + 10, cursorY + 35);
    cursorY += 44 + 18;
  }

  // 3. Grade / Rate / Qty — the full input from the factory relations officer
  doc.setFontSize(11);
  doc.setTextColor(15, 34, 56);
  doc.setFont(undefined, 'bold');
  doc.text('Available for Sale', marginX, cursorY);
  cursorY += 14;

  factory.grades.forEach((g) => {
    const rowH = 62;
    if (cursorY + rowH > h - 80) {
      doc.addPage();
      drawWatermark(doc, w, h);
      cursorY = 40;
    }
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(231, 224, 211);
    doc.roundedRect(marginX, cursorY, contentW, rowH, 6, 6, 'FD');

    doc.setFontSize(11);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text(`Grade ${g.grade}`, marginX + 10, cursorY + 18);

    doc.setFontSize(7);
    doc.setTextColor(120, 113, 108);
    doc.setFont(undefined, 'normal');
    doc.text('Rate Incl GST', marginX + contentW - 10, cursorY + 10, { align: 'right' });
    doc.setFontSize(13);
    doc.setTextColor(180, 83, 9);
    doc.setFont(undefined, 'bold');
    doc.text(`${RS}${g.rateInclGst.toLocaleString('en-IN')}/MT`, marginX + contentW - 10, cursorY + 24, { align: 'right' });

    const cols = [
      ['Rate Excl GST', `${RS}${g.rateExclGst.toLocaleString('en-IN')}/MT`],
      ['Payment Date', fmtDate(g.paymentDate)],
      ['Lifting Date', fmtDate(g.liftingDate)],
      ['Available Qty', g.availableQty > 0 ? `${g.availableQty} MT` : 'Sold out'],
    ];
    const colW = (contentW - 20) / 4;
    cols.forEach(([label, value], i) => {
      const x = marginX + 10 + i * colW;
      doc.setFontSize(7);
      doc.setTextColor(120, 113, 108);
      doc.setFont(undefined, 'normal');
      doc.text(label, x, cursorY + 36);
      doc.setFontSize(9.5);
      doc.setTextColor(15, 34, 56);
      doc.setFont(undefined, 'bold');
      doc.text(value, x, cursorY + 50, { maxWidth: colW - 6 });
    });

    doc.link(marginX, cursorY, contentW, rowH, { url: 'https://www.sugarhathi.com/login-authentication' });
    cursorY += rowH + 10;
  });
  cursorY += 6;

  // 6. Past 12 months quota table
  if (factory.past12 && factory.past12.length > 0) {
    if (cursorY > h - 140) {
      doc.addPage();
      drawWatermark(doc, w, h);
      cursorY = 40;
    }
    doc.setFontSize(11);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text('Past 12 Months Quota', marginX, cursorY);
    cursorY += 14;

    const colMonthX = marginX;
    const colMtX = w - marginX - 6;
    const rowH2 = 15.5;
    doc.setFillColor(245, 237, 221);
    doc.rect(marginX, cursorY - 11, contentW, 16, 'F');
    doc.setFontSize(7.5);
    doc.setTextColor(120, 113, 108);
    doc.setFont(undefined, 'bold');
    doc.text('Month', colMonthX + 2, cursorY);
    doc.text('Allocation (MT)', colMtX, cursorY, { align: 'right' });
    cursorY += 15;

    factory.past12.slice().reverse().forEach((r, i) => {
      if (cursorY > h - 60) {
        doc.addPage();
        drawWatermark(doc, w, h);
        cursorY = 40;
      }
      if (i % 2 === 1) {
        doc.setFillColor(253, 246, 233);
        doc.rect(marginX, cursorY - 11, contentW, rowH2, 'F');
      }
      doc.setFontSize(8.5);
      doc.setTextColor(68, 64, 60);
      doc.setFont(undefined, 'normal');
      doc.text(r.month, colMonthX + 2, cursorY);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(15, 34, 56);
      doc.text(r.qty.toLocaleString('en-IN'), colMtX, cursorY, { align: 'right' });
      cursorY += rowH2;
    });
    cursorY += 10;
  }

  // 7. Recent news
  if (factory.news && factory.news.length > 0) {
    if (cursorY > h - 100) {
      doc.addPage();
      drawWatermark(doc, w, h);
      cursorY = 40;
    }
    doc.setFontSize(11);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text('Recent News', marginX, cursorY);
    cursorY += 16;
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    factory.news.forEach((n) => {
      if (cursorY > h - 70) {
        doc.addPage();
        drawWatermark(doc, w, h);
        cursorY = 40;
      }
      const lines = doc.splitTextToSize(`\u2022 ${n.title}`, contentW - 10);
      doc.setTextColor(29, 78, 216);
      lines.forEach((line) => { doc.textWithLink(line, marginX, cursorY, { url: n.url }); cursorY += 12; });
      cursorY += 4;
    });
    cursorY += 4;
  }

  // SugarHathi Summary — placed here, just below Recent News, matching where the live
  // factory page shows it (see mandi/factory/index.jsx: "moved here, just below Recent
  // News"). Previously rendered right after the Quota box instead, near the top — out of
  // sync with the page it's meant to mirror.
  if (factory.metrics) {
    if (cursorY > h - 100) {
      doc.addPage();
      drawWatermark(doc, w, h);
      cursorY = 40;
    }
    const m = factory.metrics;
    const summaryText = `Allocation ${m.latest_qty_mt < m.max_qty_mt ? 'declined from a peak of' : 'reached a new peak of'} ${Number(m.max_qty_mt).toLocaleString('en-IN')} MT (${new Date(m.max_qty_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}) to ${Number(m.latest_qty_mt).toLocaleString('en-IN')} MT in ${new Date(m.latest_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}.` +
      (factory.ranks ? ` Ranks ${factory.ranks.national_rank} of ${factory.ranks.totalMills} nationally${factory.ranks.state_rank ? `, ${factory.ranks.state_rank} within ${factory.state}` : ''}.` : '');
    const lines = doc.splitTextToSize(summaryText, contentW - 20);
    const boxH = 26 + lines.length * 12;
    if (cursorY + boxH > h - 60) {
      doc.addPage();
      drawWatermark(doc, w, h);
      cursorY = 40;
    }
    doc.setFillColor(231, 247, 239);
    doc.setDrawColor(183, 232, 206);
    doc.roundedRect(marginX, cursorY, contentW, boxH, 6, 6, 'FD');
    doc.setFontSize(9);
    doc.setTextColor(146, 64, 14);
    doc.setFont(undefined, 'bold');
    doc.text('SUGARHATHI SUMMARY', marginX + 10, cursorY + 16);
    doc.setFontSize(9);
    doc.setTextColor(54, 83, 20);
    doc.setFont(undefined, 'normal');
    lines.forEach((line, i) => doc.text(line, marginX + 10, cursorY + 30 + i * 12, { maxWidth: contentW - 20 }));
    cursorY += boxH + 14;
  }

  // 5. Distance table
  const { distances: nearbyMills } = await fetchDistrictMillDistances(factory.millId);
  cursorY = drawNearbyMillsDistance(doc, w, h, marginX, contentW, cursorY + 6, nearbyMills, factory.name);

  // 8. SugarHathi service buttons — shared block, not re-implemented
  if (cursorY > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    cursorY = 40;
  }
  cursorY = drawCTAButtons(doc, marginX, contentW, cursorY + 10);

  // 9. Footer note
  drawFooter(doc, w, h, [
    'Rates are entered and approved by the factory\u2019s relations officer and may change without notice.',
    'Sign in on SugarHathi to lock a rate and complete a purchase.',
  ]);

  // Same clean naming convention as the Market Intelligence factory PDF
  // (SugarHathi_<Mill_Name>_Quota.pdf) — this one used a raw mill-code-based
  // name instead ("sugarhathi-mandi-39401.pdf"), inconsistent with every
  // other export in the app.
  const fileName = `SugarHathi_${shortName(factory.name)}_Mandi.pdf`;
  if (action === 'download') {
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    await shareOrDownloadBlob(blob, fileName, 'application/pdf', factory.name, `${factory.name} — live sugar rates on SugarHathi Mandi.`);
  }
}

